"use client";

import React, { useState, useRef, useEffect } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { setCurrentUser } from "../store/actions/authActions.js";
import { useTenant } from "../context/Tenant/TenantContext.js";
import { getBrandTheme } from "../utils/tenantTheme.js";
import useDrawerBackdropCleanup from "../utils/useDrawerBackdropCleanup.js";
import { Loader } from "../components/Loader.js";
import { Modal, Button } from "flowbite-react";

import AdminRiskRegistry from "./admins/AdminRiskRegistry.tsx";
import AdminRiskResponseScoring from "./admins/AdminRiskResponseScoring.tsx";
import AdminRiskAssessment from "./admins/AdminRiskAssessment.tsx";
import AdminRiskSelection from "./admins/AdminRiskSelection.tsx";
import AdminRiskScoring from "./admins/AdminRiskScoring.tsx";
import AdminRiskFinalSelection from "./admins/AdminRiskFinalSelection.tsx";
import AdminInitialResponse from "./admins/AdminInitialResponse.tsx";
import AdminResponseReview from "./admins/AdminResponseReview.tsx";
import AdminRiskResponseFinalization from "./admins/AdminRiskResponseFinalization.tsx";
import AdminRiskManagement from "./admins/AdminRiskManagement.tsx";
import AdminRiskReassessment from "./admins/AdminRiskReassessment.tsx";
import AdminAssessmentFinalization from "./admins/AdminAssessmentFinalization.tsx";
import AdminMonitoringPlan from "./admins/AdminMonitoringPlan.tsx";
import { Logout } from "../context/Auth/AuthContext.js";
import AdminKickoffPhase from "./admins/AdminKickoffPhase.js";
import AdminDashboard from "./admins/AdminDashboard.js";
import instance from "../api/api.js";
import AdminProfile from "./admins/AdminProfile.js";
import AdminSettings from "./admins/AdminSettings.js";
import AuditTrail from "./admins/AuditTrail.js";
import Users from "./admins/Users.js";

// ─── Theme Definitions ─────────────────────────────────────────────────────────
// Blueprint = dark navy (dark mode feel)
// Obsidian  = near-black (ultra dark)
// Daylight  = light sidebar, blue topbar (light mode feel)
const THEMES = {
  blueprint: {
    sb: '#1c3562', sbt: '#7d9cc4', sba: '#ffffff',
    sbab: 'rgba(255,255,255,0.12)', sbhov: 'rgba(255,255,255,0.07)',
    sbb: 'rgba(255,255,255,0.09)', sbsc: '#4a6a9e',
    tb: '#ffffff', tbt: '#0f172a', tbb: '#e8ecf0', tbst: '#64748b',
    cb: '#f1f5f9',
    pri: '#1d4ed8',
    avatarBg: '#fff7ed', avatarCol: '#ea580c',
  },
  obsidian: {
    sb: '#0c1220', sbt: '#4a5e78', sba: '#e2e8f0',
    sbab: 'rgba(255,255,255,0.08)', sbhov: 'rgba(255,255,255,0.04)',
    sbb: 'rgba(255,255,255,0.06)', sbsc: '#2a3a50',
    tb: '#ffffff', tbt: '#0f172a', tbb: '#f0f2f5', tbst: '#64748b',
    cb: '#fafafa',
    pri: '#3b82f6',
    avatarBg: '#eff6ff', avatarCol: '#2563eb',
  },
  daylight: {
    sb: '#f8fafc', sbt: '#5e7490', sba: '#0369a1',
    sbab: '#e0f2fe', sbhov: '#f0f9ff',
    sbb: '#e2e8f0', sbsc: '#94a3b8',
    tb: '#1e40af', tbt: '#ffffff', tbb: '#2552c8', tbst: 'rgba(255,255,255,0.72)',
    cb: '#eef2f7',
    pri: '#0284c7',
    avatarBg: '#fff7ed', avatarCol: '#ea580c',
  },
};

// ─── Page breadcrumb lookup ────────────────────────────────────────────────────
const PAGE_INFO = {
  '':                            { title: 'Dashboard',               c0: null,               c1: null },
  'dashboard':                   { title: 'Dashboard',               c0: null,               c1: null },
  'adminriskregistry':           { title: 'Risk Entry',              c0: 'Risk Registration', c1: 'Entry' },
  'adminriskselection':          { title: 'Risk Selection',          c0: 'Risk Registration', c1: 'Selection' },
  'adminriskscoring':            { title: 'Invite Comments',         c0: 'Risk Registration', c1: 'Invite Comments' },
  'adminriskfinalselection':     { title: 'Final Selection',         c0: 'Risk Registration', c1: 'Final Selection' },
  'adminriskassessment':         { title: 'Risk Assessment',         c0: 'Risk Assessment',   c1: 'Assessment' },
  'adminassessmentfinalization': { title: 'Assessment Finalization', c0: 'Risk Assessment',   c1: 'Finalization' },
  'admininitialresponse':        { title: 'Initial Response',        c0: 'Risk Response',     c1: 'Initial Response' },
  'adminresponsereview':         { title: 'Review & Open Comments',  c0: 'Risk Response',     c1: 'Review & Open' },
  'adminriskresponsescoring':    { title: 'Invite Comments',         c0: 'Risk Response',     c1: 'Invite Comments' },
  'adminriskresponsefinalization': { title: 'Response Finalization', c0: 'Risk Response',     c1: 'Finalization' },
  'adminmonitoringplan':         { title: 'Monitoring Plan',         c0: 'Risk Response',     c1: 'Monitoring Plan' },
  'adminriskmanagement':         { title: 'Monitoring Actions',      c0: null,               c1: null },
  'adminriskreassessment':       { title: 'Risk Reassessment',       c0: null,               c1: null },
  'adminsettings':               { title: 'Settings',                c0: null,               c1: null },
  'audittrail':                  { title: 'Audit Trail',             c0: null,               c1: null },
  'adminprofile':                { title: 'Profile',                 c0: null,               c1: null },
  'adminkickoffphase':           { title: 'Kickoff Phase',           c0: null,               c1: null },
  'users':                       { title: 'Users',                   c0: 'Users',            c1: 'Users' },
};

// ─── Sub-components ────────────────────────────────────────────────────────────

function StepBadge({ n, color, sbColor }) {
  return (
    <div style={{
      width: 18, height: 18, minWidth: 18, borderRadius: '50%', background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9, fontWeight: 800, color: '#fff', flexShrink: 0,
      boxShadow: `0 0 0 2px ${sbColor}`,
    }}>
      {n}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars -- theme picker hidden for now
function ThemeDot({ color, label, isActive, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      title={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 14, height: 14, borderRadius: '50%',
        background: color,
        cursor: 'pointer',
        flexShrink: 0,
        border: isActive ? '2px solid #fff' : '2px solid rgba(255,255,255,0.45)',
        boxShadow: isActive
          ? '0 0 0 2px rgba(255,255,255,0.85)'
          : hovered
          ? '0 0 0 2px rgba(255,255,255,0.55)'
          : 'none',
        transform: hovered ? 'scale(1.35)' : isActive ? 'scale(1.15)' : 'scale(1)',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
      }}
    />
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function AdminWrapper() {
  const { tenant, loading: tenantLoading } = useTenant();
  const location = useLocation();

  // Theme — persisted to localStorage
  const [themeName, setThemeName] = useState(
    () => localStorage.getItem('riskTheme') || 'daylight'
  );
  const t = getBrandTheme(tenant?.branding, THEMES[themeName] || THEMES.daylight);
  useDrawerBackdropCleanup();
  // eslint-disable-next-line no-unused-vars -- theme picker hidden for now
  const applyTheme = (name) => {
    setThemeName(name);
    localStorage.setItem('riskTheme', name);
  };

  // Sidebar collapse
  const [collapsed, setCollapsed] = useState(false);
  const sbW = collapsed ? 68 : 240;
  const showL = !collapsed;

  // Nav section expand/collapse
  const [regOpen,  setRegOpen]  = useState(true);
  const [assOpen,  setAssOpen]  = useState(true);
  const [respOpen, setRespOpen] = useState(false);

  // Avatar dropdown (React-controlled)
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Report generation
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showReportConfirm, setShowReportConfirm]   = useState(false);

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      const response = await instance.get('/api/admin/export/risks', { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', 'Risk_Report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Breadcrumb / page title
  const seg = location.pathname.split('/').pop().toLowerCase();
  const pageInfo = PAGE_INFO[seg] ?? PAGE_INFO['dashboard'];

  // User initials for avatar
  const storedName = localStorage.getItem('name') || 'Admin User';
  const initials = storedName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'A';

  // Shared inline-style helpers
  const niStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 8px', borderRadius: 7, cursor: 'pointer',
    marginBottom: 1, textDecoration: 'none',
    background: isActive ? t.sbab : 'transparent',
  });
  const niText = (isActive) => ({
    fontSize: 13,
    color: isActive ? t.sba : t.sbt,
    fontWeight: isActive ? 600 : 400,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  });
  const topNavItem = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
    marginBottom: 2, textDecoration: 'none',
    background: isActive ? t.sbab : 'transparent',
  });

  if (tenantLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader size="xl" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex', height: '100vh', overflow: 'hidden',
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        WebkitFontSmoothing: 'antialiased',
        // CSS vars for hover effects (used by .sb-item:hover in index.css)
        '--sb-hover': t.sbhov,
      }}
    >
      {/* ═══════════════════════════ SIDEBAR ════════════════════════════════ */}
      <div style={{
        width: sbW, minWidth: sbW,
        background: t.sb,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', flexShrink: 0,
        borderRight: `1px solid ${t.sbb}`,
        transition: 'width 0.2s ease',
      }}>

        {/* Logo + collapse button */}
        <div style={{
          height: 60, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 14px',
          borderBottom: `1px solid ${t.sbb}`, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden' }}>
            <div style={{
              width: 32, height: 32, minWidth: 32, borderRadius: 8,
              overflow: 'hidden', flexShrink: 0,
              background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <img src={tenant?.branding?.logoUrl || "/rsmLogo.png"} alt="logo" style={{ width: 28, height: 28, objectFit: 'contain' }} />
            </div>
            {showL && (
              <span style={{
                color: t.sba, fontSize: 14, fontWeight: 700,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                letterSpacing: '-0.2px',
              }}>
                {tenant?.companyName || 'RSM Botswana'}
              </span>
            )}
          </div>
          <button
            onClick={() => setCollapsed(c => !c)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: t.sbt, padding: 4, borderRadius: 4, flexShrink: 0,
              fontSize: 13, lineHeight: 1,
            }}
          >
            {collapsed ? '→' : '←'}
          </button>
        </div>

        {/* Nav area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>

          {/* Dashboard */}
          <NavLink to="dashboard" style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <div className="sb-item" style={topNavItem(isActive)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                  style={{ flexShrink: 0, color: isActive ? t.sba : t.sbt }}>
                  <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
                  <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
                  <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.5"/>
                  <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.5"/>
                </svg>
                {showL && <span style={niText(isActive)}>Dashboard</span>}
              </div>
            )}
          </NavLink>

          <div style={{ height: 1, background: t.sbb, margin: '8px 4px' }} />

          {/* ── Risk Registration ── */}
          <div
            className="sb-item"
            onClick={() => { setRegOpen(o => !o); if (collapsed) setCollapsed(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', marginBottom: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: t.sbsc }}>
              <rect x="2" y="1" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M5 5h6M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {showL && (
              <>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: t.sbsc }}>
                  Risk Registration
                </span>
                <span style={{ fontSize: 11, color: t.sbsc }}>{regOpen ? '▾' : '▸'}</span>
              </>
            )}
          </div>

          {regOpen && (
            <div style={{ marginLeft: 10, paddingLeft: 14, borderLeft: `1px solid ${t.sbb}`, marginBottom: 4 }}>
              <NavLink to="adminriskregistry" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={1} color="#3b82f6" sbColor={t.sb}/>
                    {showL && <span style={niText(isActive)}>Entry</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminriskselection" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={2} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={niText(isActive)}>Selection</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminriskscoring" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={3} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Invite Comments</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminriskfinalselection" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={4} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={niText(isActive)}>Final Selection</span>}
                  </div>
                )}
              </NavLink>
            </div>
          )}

          {/* ── Risk Assessment ── */}
          <div
            className="sb-item"
            onClick={() => { setAssOpen(o => !o); if (collapsed) setCollapsed(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', marginBottom: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: t.sbsc }}>
              <rect x="1" y="9" width="4" height="6" rx="1" fill="currentColor" opacity="0.5"/>
              <rect x="6" y="5" width="4" height="10" rx="1" fill="currentColor" opacity="0.7"/>
              <rect x="11" y="1" width="4" height="14" rx="1" fill="currentColor"/>
            </svg>
            {showL && (
              <>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: t.sbsc }}>
                  Risk Assessment
                </span>
                <span style={{ fontSize: 11, color: t.sbsc }}>{assOpen ? '▾' : '▸'}</span>
              </>
            )}
          </div>

          {assOpen && (
            <div style={{ marginLeft: 10, paddingLeft: 14, borderLeft: `1px solid ${t.sbb}`, marginBottom: 4 }}>
              <NavLink to="adminriskassessment" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={1} color="#3b82f6" sbColor={t.sb}/>
                    {showL && <span style={niText(isActive)}>Assessment</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminassessmentfinalization" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={2} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Finalization</span>}
                  </div>
                )}
              </NavLink>
            </div>
          )}

          {/* ── Risk Response ── */}
          <div
            className="sb-item"
            onClick={() => { setRespOpen(o => !o); if (collapsed) setCollapsed(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', marginBottom: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: t.sbsc }}>
              <path d="M8 1L14 4v5c0 3-3 5-6 6-3-1-6-3-6-6V4l6-3z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.12"/>
            </svg>
            {showL && (
              <>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: t.sbsc }}>
                  Risk Response
                </span>
                <span style={{ fontSize: 11, color: t.sbsc }}>{respOpen ? '▾' : '▸'}</span>
              </>
            )}
          </div>

          {respOpen && (
            <div style={{ marginLeft: 10, paddingLeft: 14, borderLeft: `1px solid ${t.sbb}`, marginBottom: 4 }}>
              <NavLink to="adminInitialResponse" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={1} color="#3b82f6" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Initial Response</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminresponsereview" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={2} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Review &amp; Open</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminriskresponsescoring" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={3} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Invite Comments</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminriskresponsefinalization" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={4} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Finalization</span>}
                  </div>
                )}
              </NavLink>
              <NavLink to="adminmonitoringplan" style={{ textDecoration: 'none' }}>
                {({ isActive }) => (
                  <div className="sb-item" style={niStyle(isActive)}>
                    <StepBadge n={5} color="#f59e0b" sbColor={t.sb}/>
                    {showL && <span style={{ ...niText(isActive), overflow: 'hidden', textOverflow: 'ellipsis' }}>Monitoring Plan</span>}
                  </div>
                )}
              </NavLink>
            </div>
          )}

          {/* ── Monitoring Actions ── */}
          <NavLink to="adminriskmanagement" style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <div className="sb-item" style={topNavItem(isActive)}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"
                  style={{ flexShrink: 0, color: isActive ? t.sba : t.sbt }}>
                  <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="5" cy="11" r="1" fill="currentColor"/>
                  <circle cx="8" cy="11" r="1" fill="currentColor"/>
                  <circle cx="11" cy="11" r="1" fill="currentColor"/>
                </svg>
                {showL && <span style={niText(isActive)}>Monitoring Actions</span>}
              </div>
            )}
          </NavLink>

          {/* ── Reassessment ── */}
          <NavLink to="adminriskreassessment" style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <div className="sb-item" style={topNavItem(isActive)}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"
                  style={{ flexShrink: 0, color: isActive ? t.sba : t.sbt }}>
                  <path d="M14 8A6 6 0 012 8a6 6 0 016-6c2 0 3.5.7 4.8 2"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M14 4V2h-2" stroke="currentColor" strokeWidth="1.5"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {showL && <span style={niText(isActive)}>Reassessment</span>}
              </div>
            )}
          </NavLink>

          <div style={{ height: 1, background: t.sbb, margin: '8px 4px' }} />

          {/* ── Users ── */}
          <NavLink to="users" style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <div className="sb-item" style={topNavItem(isActive)}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"
                  style={{ flexShrink: 0, color: isActive ? t.sba : t.sbt }}>
                  <circle cx="6" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M1 14c0-3 2.3-5 5-5s5 2 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="12.5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" opacity="0.55"/>
                  <path d="M12.5 10c1.5 0 3 1 3 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.55"/>
                </svg>
                {showL && <span style={niText(isActive)}>Users</span>}
              </div>
            )}
          </NavLink>

          {/* ── Audit Trail ── */}
          <NavLink to="audittrail" style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <div className="sb-item" style={topNavItem(isActive)}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"
                  style={{ flexShrink: 0, color: isActive ? t.sba : t.sbt }}>
                  <rect x="2.5" y="1.5" width="11" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M5 5h6M5 8h6M5 11h3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {showL && <span style={niText(isActive)}>Audit Trail</span>}
              </div>
            )}
          </NavLink>

          {/* ── Settings ── */}
          <NavLink to="adminsettings" style={{ textDecoration: 'none' }}>
            {({ isActive }) => (
              <div className="sb-item" style={topNavItem(isActive)}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"
                  style={{ flexShrink: 0, color: isActive ? t.sba : t.sbt }}>
                  <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {showL && <span style={niText(isActive)}>Settings</span>}
              </div>
            )}
          </NavLink>
        </div>

        {/* Generate Report — pinned at bottom */}
        <div style={{ padding: 12, borderTop: `1px solid ${t.sbb}`, flexShrink: 0 }}>
          <button
            onClick={() => setShowReportConfirm(true)}
            disabled={isGeneratingReport}
            style={{
              width: '100%', padding: '10px 12px',
              background: t.pri, border: 'none', borderRadius: 8,
              color: '#fff', fontSize: 12, fontWeight: 700,
              cursor: isGeneratingReport ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              opacity: isGeneratingReport ? 0.6 : 1,
              transition: 'opacity 0.15s',
              fontFamily: 'inherit',
            }}
          >
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M6 2a2 2 0 0 0-2 2v15a3 3 0 0 0 3 3h12a1 1 0 1 0 0-2h-2v-2h2a1 1 0 0 0 1-1V4a2 2 0 0 0-2-2h-8v16h5v2H7a1 1 0 1 1 0-2h1V2H6Z" clipRule="evenodd"/>
            </svg>
            {showL && <span>{isGeneratingReport ? 'Generating…' : 'Generate Report'}</span>}
          </button>
        </div>
      </div>

      {/* ═══════════════════════════ MAIN PANEL ═════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* ── Topbar ── */}
        <div style={{
          height: 60, background: t.tb,
          borderBottom: `1px solid ${t.tbb}`,
          display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16, flexShrink: 0,
        }}>
          {/* Breadcrumb + Title */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {pageInfo.c0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 1 }}>
                <span style={{ fontSize: 11, color: t.tbst, whiteSpace: 'nowrap' }}>{pageInfo.c0}</span>
                <span style={{ fontSize: 10, color: t.tbst }}>›</span>
                <span style={{ fontSize: 11, color: t.tbst, whiteSpace: 'nowrap' }}>{pageInfo.c1}</span>
              </div>
            )}
            <h1 style={{
              fontSize: 17, fontWeight: 800, color: t.tbt, margin: 0,
              letterSpacing: '-0.4px', lineHeight: 1.2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {pageInfo.title}
            </h1>
          </div>

          {/* Theme picker — hidden for now
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
            background: 'rgba(255,255,255,0.13)',
            border: '1px solid rgba(255,255,255,0.22)',
            borderRadius: 100,
            padding: '5px 12px',
          }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
              textTransform: 'uppercase', color: t.tbt, opacity: 0.7,
              userSelect: 'none',
            }}>
              Theme
            </span>
            {[
              { key: 'blueprint', color: '#1c3562', label: 'Blueprint' },
              { key: 'obsidian',  color: '#0c1220', label: 'Obsidian'  },
              { key: 'daylight',  color: '#1e40af', label: 'Daylight'  },
            ].map(({ key, color, label }) => (
              <ThemeDot
                key={key}
                color={color}
                label={label}
                isActive={themeName === key}
                onClick={() => applyTheme(key)}
              />
            ))}
          </div>
          */}

          {/* Avatar + dropdown */}
          <div ref={avatarRef} style={{ position: 'relative', flexShrink: 0 }}>
            <div
              onClick={() => setAvatarOpen(o => !o)}
              style={{
                width: 34, height: 34, borderRadius: '50%',
                background: t.avatarBg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 13, fontWeight: 800,
                color: t.avatarCol, cursor: 'pointer',
                border: `2px solid ${t.tbb}`,
                userSelect: 'none',
              }}
            >
              {initials}
            </div>
            {avatarOpen && (
              <div style={{
                position: 'absolute', top: 42, right: 0, width: 210,
                background: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                zIndex: 200, overflow: 'hidden',
              }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{storedName}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{localStorage.getItem('email')}</div>
                  <div style={{ marginTop: 6 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', background: '#fff7ed', borderRadius: 100,
                      fontSize: 11, fontWeight: 700, color: '#ea580c',
                    }}>
                      Administrator
                    </span>
                  </div>
                </div>
                <div style={{ padding: '4px 0' }}>
                  <a
                    href="/admin/adminprofile"
                    className="avatar-menu-item"
                    style={{ display: 'block', padding: '8px 16px', fontSize: 13, color: '#374151', textDecoration: 'none' }}
                    onClick={() => setAvatarOpen(false)}
                  >
                    Profile
                  </a>
                  <button
                    onClick={() => { setAvatarOpen(false); Logout(); }}
                    className="avatar-menu-item"
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '8px 16px', fontSize: 13, color: '#dc2626',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Content ── */}
        <main style={{ flex: 1, overflowY: 'auto', padding: 24, background: t.cb }}>
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard"                   element={<AdminDashboard />} />
            <Route path="adminriskregistry"           element={<AdminRiskRegistry />} />
            <Route path="adminriskscoring"            element={<AdminRiskScoring />} />
            <Route path="admininitialresponse"        element={<AdminInitialResponse />} />
            <Route path="adminriskfinalselection"     element={<AdminRiskFinalSelection />} />
            <Route path="adminriskselection"          element={<AdminRiskSelection />} />
            <Route path="adminresponsereview"         element={<AdminResponseReview />} />
            <Route path="adminriskresponsescoring"    element={<AdminRiskResponseScoring />} />
            <Route path="adminriskresponsefinalization" element={<AdminRiskResponseFinalization />} />
            <Route path="adminriskmanagement"         element={<AdminRiskManagement />} />
            <Route path="adminriskassessment"         element={<AdminRiskAssessment />} />
            <Route path="adminkickoffphase"           element={<AdminKickoffPhase />} />
            <Route path="adminriskreassessment"       element={<AdminRiskReassessment />} />
            <Route path="adminassessmentfinalization" element={<AdminAssessmentFinalization />} />
            <Route path="adminmonitoringplan"         element={<AdminMonitoringPlan />} />
            <Route path="adminsettings"               element={<AdminSettings />} />
            <Route path="audittrail"                  element={<AuditTrail />} />
            <Route path="adminprofile"                element={<AdminProfile />} />
            <Route path="users"                       element={<Users />} />
          </Routes>
        </main>
      </div>

      {/* ── Generate Report Confirmation Modal ── */}
      <Modal
        dismissible
        show={showReportConfirm}
        size="xl"
        onClose={() => setShowReportConfirm(false)}
        popup
      >
        <Modal.Header className="ml-3 p-5 border-b">
          <h4 className="font-bold text-gray-700">Generate Report</h4>
        </Modal.Header>
        <Modal.Footer className="border-t flex justify-end">
          <Button color="gray" className="border-none text-riskRed" onClick={() => setShowReportConfirm(false)}>
            Cancel
          </Button>
          <Button
            className="bg-riskRed hover:bg-riskRed"
            onClick={() => { setShowReportConfirm(false); handleGenerateReport(); }}
          >
            {isGeneratingReport ? 'Generating…' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => ({ user: state.userData.user });
const mapDispatchToProps = (dispatch) => ({ setUser: (user) => dispatch(setCurrentUser(user)) });

export default connect(mapStateToProps, mapDispatchToProps)(AdminWrapper);
